import { ERROR_CODES, ErrorCode } from './errors';

// Error message templates for different languages
export interface ErrorMessageTemplate {
  title: string;
  message: string;
  action?: string;
  suggestion?: string;
  field?: string;
}

export interface LocalizedErrorMessages {
  [key: string]: {
    [code: string]: ErrorMessageTemplate;
  };
}

// English error messages (default)
const enErrorMessages: Record<string, ErrorMessageTemplate> = {
  // Validation errors
  [ERROR_CODES.VALIDATION_ERROR]: {
    title: 'Validation Error',
    message: 'The provided data is invalid.',
    action: 'Please check your input and try again.',
    suggestion: 'Review the highlighted fields for errors.'
  },
  [ERROR_CODES.REQUIRED_FIELD]: {
    title: 'Required Field',
    message: 'This field is required.',
    action: 'Please fill in this field.',
    suggestion: 'All required fields must be completed.'
  },
  [ERROR_CODES.INVALID_EMAIL]: {
    title: 'Invalid Email',
    message: 'Please enter a valid email address.',
    action: 'Check the email format and try again.',
    suggestion: 'Email should be in format: user@example.com'
  },
  [ERROR_CODES.INVALID_PHONE]: {
    title: 'Invalid Phone Number',
    message: 'Please enter a valid phone number.',
    action: 'Check the phone number format and try again.',
    suggestion: 'Phone number should include country code if applicable.'
  },
  [ERROR_CODES.INVALID_DATE]: {
    title: 'Invalid Date',
    message: 'Please enter a valid date.',
    action: 'Check the date format and try again.',
    suggestion: 'Date should be in format: YYYY-MM-DD'
  },
  [ERROR_CODES.INVALID_FORMAT]: {
    title: 'Invalid Format',
    message: 'The data format is not valid.',
    action: 'Please use the correct format.',
    suggestion: 'Check the field requirements and format guidelines.'
  },
  [ERROR_CODES.FIELD_TOO_SHORT]: {
    title: 'Field Too Short',
    message: 'This field is too short.',
    action: 'Please provide more information.',
    suggestion: 'Minimum length requirements must be met.'
  },
  [ERROR_CODES.FIELD_TOO_LONG]: {
    title: 'Field Too Long',
    message: 'This field is too long.',
    action: 'Please shorten your input.',
    suggestion: 'Maximum length limits must be respected.'
  },
  [ERROR_CODES.INVALID_CHOICE]: {
    title: 'Invalid Choice',
    message: 'Please select a valid option.',
    action: 'Choose from the available options.',
    suggestion: 'Only predefined values are allowed.'
  },

  // Authentication errors
  [ERROR_CODES.AUTHENTICATION_ERROR]: {
    title: 'Authentication Error',
    message: 'Authentication failed.',
    action: 'Please log in again.',
    suggestion: 'Check your credentials and try again.'
  },
  [ERROR_CODES.INVALID_CREDENTIALS]: {
    title: 'Invalid Credentials',
    message: 'Username or password is incorrect.',
    action: 'Please check your credentials and try again.',
    suggestion: 'Make sure caps lock is off and check for typos.'
  },
  [ERROR_CODES.TOKEN_EXPIRED]: {
    title: 'Session Expired',
    message: 'Your session has expired.',
    action: 'Please log in again.',
    suggestion: 'For security, sessions expire after a period of inactivity.'
  },
  [ERROR_CODES.TOKEN_INVALID]: {
    title: 'Invalid Session',
    message: 'Your session is invalid.',
    action: 'Please log in again.',
    suggestion: 'This may happen if you logged in on another device.'
  },
  [ERROR_CODES.SESSION_EXPIRED]: {
    title: 'Session Expired',
    message: 'Your session has expired due to inactivity.',
    action: 'Please log in again.',
    suggestion: 'For security, sessions expire after 30 minutes of inactivity.'
  },
  [ERROR_CODES.ACCOUNT_LOCKED]: {
    title: 'Account Locked',
    message: 'Your account has been temporarily locked.',
    action: 'Please contact support or try again later.',
    suggestion: 'Accounts are locked after multiple failed login attempts.'
  },
  [ERROR_CODES.TOO_MANY_ATTEMPTS]: {
    title: 'Too Many Attempts',
    message: 'Too many failed login attempts.',
    action: 'Please wait before trying again.',
    suggestion: 'Try again in 15 minutes or reset your password.'
  },

  // Authorization errors
  [ERROR_CODES.AUTHORIZATION_ERROR]: {
    title: 'Access Denied',
    message: 'You do not have permission to access this resource.',
    action: 'Please contact your administrator.',
    suggestion: 'You may need additional permissions to perform this action.'
  },
  [ERROR_CODES.INSUFFICIENT_PERMISSIONS]: {
    title: 'Insufficient Permissions',
    message: 'You do not have the required permissions.',
    action: 'Please contact your administrator for access.',
    suggestion: 'Different user roles have different access levels.'
  },
  [ERROR_CODES.ACCESS_DENIED]: {
    title: 'Access Denied',
    message: 'Access to this resource is denied.',
    action: 'Please contact support if you believe this is an error.',
    suggestion: 'This resource may be restricted or no longer available.'
  },
  [ERROR_CODES.ROLE_REQUIRED]: {
    title: 'Role Required',
    message: 'A specific role is required for this action.',
    action: 'Please contact your administrator.',
    suggestion: 'You may need to be assigned a different role.'
  },

  // Not found errors
  [ERROR_CODES.NOT_FOUND_ERROR]: {
    title: 'Not Found',
    message: 'The requested resource was not found.',
    action: 'Please check the URL or search for the resource.',
    suggestion: 'The resource may have been moved or deleted.'
  },
  [ERROR_CODES.USER_NOT_FOUND]: {
    title: 'User Not Found',
    message: 'The specified user was not found.',
    action: 'Please check the user ID or search for the user.',
    suggestion: 'The user may have been deleted or the ID may be incorrect.'
  },
  [ERROR_CODES.RESOURCE_NOT_FOUND]: {
    title: 'Resource Not Found',
    message: 'The requested resource was not found.',
    action: 'Please check the resource ID or search again.',
    suggestion: 'The resource may have been moved, deleted, or you may not have access.'
  },
  [ERROR_CODES.FILE_NOT_FOUND]: {
    title: 'File Not Found',
    message: 'The requested file was not found.',
    action: 'Please check the file path or upload the file again.',
    suggestion: 'The file may have been moved, deleted, or renamed.'
  },

  // Network errors
  [ERROR_CODES.NETWORK_ERROR]: {
    title: 'Network Error',
    message: 'A network error occurred.',
    action: 'Please check your internet connection and try again.',
    suggestion: 'Try refreshing the page or check your network settings.'
  },
  [ERROR_CODES.REQUEST_TIMEOUT]: {
    title: 'Request Timeout',
    message: 'The request timed out.',
    action: 'Please try again.',
    suggestion: 'The server may be busy. Try again in a few moments.'
  },
  [ERROR_CODES.CONNECTION_FAILED]: {
    title: 'Connection Failed',
    message: 'Failed to connect to the server.',
    action: 'Please check your internet connection and try again.',
    suggestion: 'Check your firewall settings or try a different network.'
  },
  [ERROR_CODES.API_UNAVAILABLE]: {
    title: 'Service Unavailable',
    message: 'The service is temporarily unavailable.',
    action: 'Please try again later.',
    suggestion: 'The service may be under maintenance or experiencing issues.'
  },

  // Server errors
  [ERROR_CODES.SERVER_ERROR]: {
    title: 'Server Error',
    message: 'An internal server error occurred.',
    action: 'Please try again later.',
    suggestion: 'If the problem persists, contact support.'
  },
  [ERROR_CODES.INTERNAL_ERROR]: {
    title: 'Internal Error',
    message: 'An unexpected error occurred.',
    action: 'Please try again or contact support.',
    suggestion: 'This error has been logged and will be investigated.'
  },
  [ERROR_CODES.SERVICE_UNAVAILABLE]: {
    title: 'Service Unavailable',
    message: 'The service is currently unavailable.',
    action: 'Please try again later.',
    suggestion: 'The service may be under maintenance or experiencing high load.'
  },
  [ERROR_CODES.MAINTENANCE_MODE]: {
    title: 'Maintenance Mode',
    message: 'The system is currently under maintenance.',
    action: 'Please try again later.',
    suggestion: 'Maintenance usually takes 30 minutes to 2 hours.'
  },

  // Database errors
  [ERROR_CODES.DATABASE_ERROR]: {
    title: 'Database Error',
    message: 'A database error occurred.',
    action: 'Please try again later.',
    suggestion: 'If the problem persists, contact support.'
  },
  [ERROR_CODES.CONNECTION_ERROR]: {
    title: 'Database Connection Error',
    message: 'Failed to connect to the database.',
    action: 'Please try again later.',
    suggestion: 'The database may be temporarily unavailable.'
  },
  [ERROR_CODES.QUERY_ERROR]: {
    title: 'Database Query Error',
    message: 'A database query failed.',
    action: 'Please try again or contact support.',
    suggestion: 'This may be due to invalid data or database constraints.'
  },
  [ERROR_CODES.CONSTRAINT_VIOLATION]: {
    title: 'Data Constraint Error',
    message: 'The data violates database constraints.',
    action: 'Please check your input and try again.',
    suggestion: 'Ensure all required fields are filled and data is valid.'
  },

  // Business logic errors
  [ERROR_CODES.BUSINESS_LOGIC_ERROR]: {
    title: 'Business Logic Error',
    message: 'The operation cannot be completed.',
    action: 'Please check the requirements and try again.',
    suggestion: 'This may be due to business rules or state constraints.'
  },
  [ERROR_CODES.INVALID_OPERATION]: {
    title: 'Invalid Operation',
    message: 'This operation is not allowed.',
    action: 'Please check the operation requirements.',
    suggestion: 'Some operations may be restricted based on your role or data state.'
  },
  [ERROR_CODES.STATE_CONFLICT]: {
    title: 'State Conflict',
    message: 'The current state does not allow this operation.',
    action: 'Please refresh the page and try again.',
    suggestion: 'The data may have been modified by another user.'
  },
  [ERROR_CODES.RESOURCE_CONFLICT]: {
    title: 'Resource Conflict',
    message: 'The resource conflicts with existing data.',
    action: 'Please check for duplicates or conflicts.',
    suggestion: 'This may happen when trying to create a resource that already exists.'
  },

  // Unknown errors
  [ERROR_CODES.UNKNOWN_ERROR]: {
    title: 'Unknown Error',
    message: 'An unexpected error occurred.',
    action: 'Please try again or contact support.',
    suggestion: 'This error has been logged and will be investigated.'
  }
};

// Spanish error messages
const esErrorMessages: Record<string, ErrorMessageTemplate> = {
  [ERROR_CODES.VALIDATION_ERROR]: {
    title: 'Error de Validación',
    message: 'Los datos proporcionados no son válidos.',
    action: 'Por favor, verifica tu entrada e intenta de nuevo.',
    suggestion: 'Revisa los campos resaltados para errores.'
  },
  [ERROR_CODES.REQUIRED_FIELD]: {
    title: 'Campo Requerido',
    message: 'Este campo es obligatorio.',
    action: 'Por favor, completa este campo.',
    suggestion: 'Todos los campos obligatorios deben completarse.'
  },
  [ERROR_CODES.INVALID_EMAIL]: {
    title: 'Email Inválido',
    message: 'Por favor, ingresa una dirección de email válida.',
    action: 'Verifica el formato del email e intenta de nuevo.',
    suggestion: 'El email debe tener el formato: usuario@ejemplo.com'
  },
  [ERROR_CODES.AUTHENTICATION_ERROR]: {
    title: 'Error de Autenticación',
    message: 'La autenticación falló.',
    action: 'Por favor, inicia sesión de nuevo.',
    suggestion: 'Verifica tus credenciales e intenta de nuevo.'
  },
  [ERROR_CODES.AUTHORIZATION_ERROR]: {
    title: 'Acceso Denegado',
    message: 'No tienes permiso para acceder a este recurso.',
    action: 'Por favor, contacta a tu administrador.',
    suggestion: 'Puedes necesitar permisos adicionales para realizar esta acción.'
  },
  [ERROR_CODES.NETWORK_ERROR]: {
    title: 'Error de Red',
    message: 'Ocurrió un error de red.',
    action: 'Por favor, verifica tu conexión a internet e intenta de nuevo.',
    suggestion: 'Intenta refrescar la página o verifica tu configuración de red.'
  },
  [ERROR_CODES.SERVER_ERROR]: {
    title: 'Error del Servidor',
    message: 'Ocurrió un error interno del servidor.',
    action: 'Por favor, intenta de nuevo más tarde.',
    suggestion: 'Si el problema persiste, contacta soporte.'
  },
  [ERROR_CODES.UNKNOWN_ERROR]: {
    title: 'Error Desconocido',
    message: 'Ocurrió un error inesperado.',
    action: 'Por favor, intenta de nuevo o contacta soporte.',
    suggestion: 'Este error ha sido registrado y será investigado.'
  }
};

// French error messages
const frErrorMessages: Record<string, ErrorMessageTemplate> = {
  [ERROR_CODES.VALIDATION_ERROR]: {
    title: 'Erreur de Validation',
    message: 'Les données fournies ne sont pas valides.',
    action: 'Veuillez vérifier votre saisie et réessayer.',
    suggestion: 'Vérifiez les champs surlignés pour les erreurs.'
  },
  [ERROR_CODES.REQUIRED_FIELD]: {
    title: 'Champ Requis',
    message: 'Ce champ est obligatoire.',
    action: 'Veuillez remplir ce champ.',
    suggestion: 'Tous les champs obligatoires doivent être complétés.'
  },
  [ERROR_CODES.INVALID_EMAIL]: {
    title: 'Email Invalide',
    message: 'Veuillez saisir une adresse email valide.',
    action: 'Vérifiez le format de l\'email et réessayez.',
    suggestion: 'L\'email doit avoir le format: utilisateur@exemple.com'
  },
  [ERROR_CODES.AUTHENTICATION_ERROR]: {
    title: 'Erreur d\'Authentification',
    message: 'L\'authentification a échoué.',
    action: 'Veuillez vous reconnecter.',
    suggestion: 'Vérifiez vos identifiants et réessayez.'
  },
  [ERROR_CODES.AUTHORIZATION_ERROR]: {
    title: 'Accès Refusé',
    message: 'Vous n\'avez pas la permission d\'accéder à cette ressource.',
    action: 'Veuillez contacter votre administrateur.',
    suggestion: 'Vous pourriez avoir besoin de permissions supplémentaires.'
  },
  [ERROR_CODES.NETWORK_ERROR]: {
    title: 'Erreur Réseau',
    message: 'Une erreur réseau s\'est produite.',
    action: 'Veuillez vérifier votre connexion internet et réessayer.',
    suggestion: 'Essayez de rafraîchir la page ou vérifiez vos paramètres réseau.'
  },
  [ERROR_CODES.SERVER_ERROR]: {
    title: 'Erreur Serveur',
    message: 'Une erreur interne du serveur s\'est produite.',
    action: 'Veuillez réessayer plus tard.',
    suggestion: 'Si le problème persiste, contactez le support.'
  },
  [ERROR_CODES.UNKNOWN_ERROR]: {
    title: 'Erreur Inconnue',
    message: 'Une erreur inattendue s\'est produite.',
    action: 'Veuillez réessayer ou contacter le support.',
    suggestion: 'Cette erreur a été enregistrée et sera examinée.'
  }
};

// German error messages
const deErrorMessages: Record<string, ErrorMessageTemplate> = {
  [ERROR_CODES.VALIDATION_ERROR]: {
    title: 'Validierungsfehler',
    message: 'Die bereitgestellten Daten sind ungültig.',
    action: 'Bitte überprüfen Sie Ihre Eingabe und versuchen Sie es erneut.',
    suggestion: 'Überprüfen Sie die hervorgehobenen Felder auf Fehler.'
  },
  [ERROR_CODES.REQUIRED_FIELD]: {
    title: 'Pflichtfeld',
    message: 'Dieses Feld ist erforderlich.',
    action: 'Bitte füllen Sie dieses Feld aus.',
    suggestion: 'Alle Pflichtfelder müssen ausgefüllt werden.'
  },
  [ERROR_CODES.INVALID_EMAIL]: {
    title: 'Ungültige E-Mail',
    message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    action: 'Überprüfen Sie das E-Mail-Format und versuchen Sie es erneut.',
    suggestion: 'E-Mail sollte das Format haben: benutzer@beispiel.com'
  },
  [ERROR_CODES.AUTHENTICATION_ERROR]: {
    title: 'Authentifizierungsfehler',
    message: 'Die Authentifizierung ist fehlgeschlagen.',
    action: 'Bitte melden Sie sich erneut an.',
    suggestion: 'Überprüfen Sie Ihre Anmeldedaten und versuchen Sie es erneut.'
  },
  [ERROR_CODES.AUTHORIZATION_ERROR]: {
    title: 'Zugriff Verweigert',
    message: 'Sie haben keine Berechtigung, auf diese Ressource zuzugreifen.',
    action: 'Bitte kontaktieren Sie Ihren Administrator.',
    suggestion: 'Sie benötigen möglicherweise zusätzliche Berechtigungen.'
  },
  [ERROR_CODES.NETWORK_ERROR]: {
    title: 'Netzwerkfehler',
    message: 'Ein Netzwerkfehler ist aufgetreten.',
    action: 'Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
    suggestion: 'Versuchen Sie, die Seite zu aktualisieren oder überprüfen Sie Ihre Netzwerkeinstellungen.'
  },
  [ERROR_CODES.SERVER_ERROR]: {
    title: 'Serverfehler',
    message: 'Ein interner Serverfehler ist aufgetreten.',
    action: 'Bitte versuchen Sie es später erneut.',
    suggestion: 'Wenn das Problem weiterhin besteht, kontaktieren Sie den Support.'
  },
  [ERROR_CODES.UNKNOWN_ERROR]: {
    title: 'Unbekannter Fehler',
    message: 'Ein unerwarteter Fehler ist aufgetreten.',
    action: 'Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.',
    suggestion: 'Dieser Fehler wurde protokolliert und wird untersucht.'
  }
};

// Arabic error messages
const arErrorMessages: Record<string, ErrorMessageTemplate> = {
  [ERROR_CODES.VALIDATION_ERROR]: {
    title: 'خطأ في التحقق',
    message: 'البيانات المقدمة غير صالحة.',
    action: 'يرجى التحقق من إدخالك والمحاولة مرة أخرى.',
    suggestion: 'راجع الحقول المميزة للأخطاء.'
  },
  [ERROR_CODES.REQUIRED_FIELD]: {
    title: 'حقل مطلوب',
    message: 'هذا الحقل مطلوب.',
    action: 'يرجى ملء هذا الحقل.',
    suggestion: 'يجب ملء جميع الحقول المطلوبة.'
  },
  [ERROR_CODES.INVALID_EMAIL]: {
    title: 'بريد إلكتروني غير صالح',
    message: 'يرجى إدخال عنوان بريد إلكتروني صالح.',
    action: 'تحقق من تنسيق البريد الإلكتروني والمحاولة مرة أخرى.',
    suggestion: 'يجب أن يكون البريد الإلكتروني بالتنسيق: مستخدم@مثال.com'
  },
  [ERROR_CODES.AUTHENTICATION_ERROR]: {
    title: 'خطأ في المصادقة',
    message: 'فشلت المصادقة.',
    action: 'يرجى تسجيل الدخول مرة أخرى.',
    suggestion: 'تحقق من بيانات الاعتماد الخاصة بك والمحاولة مرة أخرى.'
  },
  [ERROR_CODES.AUTHORIZATION_ERROR]: {
    title: 'تم رفض الوصول',
    message: 'ليس لديك إذن للوصول إلى هذا المورد.',
    action: 'يرجى الاتصال بمدير النظام.',
    suggestion: 'قد تحتاج إلى أذونات إضافية لتنفيذ هذا الإجراء.'
  },
  [ERROR_CODES.NETWORK_ERROR]: {
    title: 'خطأ في الشبكة',
    message: 'حدث خطأ في الشبكة.',
    action: 'يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى.',
    suggestion: 'حاول تحديث الصفحة أو تحقق من إعدادات الشبكة.'
  },
  [ERROR_CODES.SERVER_ERROR]: {
    title: 'خطأ في الخادم',
    message: 'حدث خطأ داخلي في الخادم.',
    action: 'يرجى المحاولة مرة أخرى لاحقاً.',
    suggestion: 'إذا استمرت المشكلة، اتصل بالدعم الفني.'
  },
  [ERROR_CODES.UNKNOWN_ERROR]: {
    title: 'خطأ غير معروف',
    message: 'حدث خطأ غير متوقع.',
    action: 'يرجى المحاولة مرة أخرى أو الاتصال بالدعم الفني.',
    suggestion: 'تم تسجيل هذا الخطأ وسيتم التحقيق فيه.'
  }
};

// Complete localized error messages
export const localizedErrorMessages: LocalizedErrorMessages = {
  en: enErrorMessages,
  es: esErrorMessages,
  fr: frErrorMessages,
  de: deErrorMessages,
  ar: arErrorMessages
};

// Error message utility functions
export const errorMessageUtils = {
  // Get error message for a specific code and language
  getMessage: (code: string, language: string = 'en'): ErrorMessageTemplate => {
    const messages = localizedErrorMessages[language] || localizedErrorMessages.en;
    return messages?.[code] || messages?.[ERROR_CODES.UNKNOWN_ERROR] || { 
      title: 'Error',
      message: 'Unknown error occurred'
    };
  },

  // Get error message with field replacement
  getMessageWithField: (code: string, field: string, language: string = 'en'): ErrorMessageTemplate => {
    const message = errorMessageUtils.getMessage(code, language);
    return {
      ...message,
      message: message.message.replace('{field}', field),
      field
    };
  },

  // Get all messages for a language
  getAllMessages: (language: string = 'en'): Record<string, ErrorMessageTemplate> => {
    return localizedErrorMessages[language] || localizedErrorMessages.en || {};
  },

  // Check if a language is supported
  isLanguageSupported: (language: string): boolean => {
    return language in localizedErrorMessages;
  },

  // Get supported languages
  getSupportedLanguages: (): string[] => {
    return Object.keys(localizedErrorMessages);
  },

  // Format error message with context
  formatMessage: (template: ErrorMessageTemplate, context?: Record<string, any>): string => {
    let message = template.message;
    
    if (context) {
      Object.keys(context).forEach(key => {
        const placeholder = `{${key}}`;
        if (message.includes(placeholder)) {
          message = message.replace(placeholder, String(context[key]));
        }
      });
    }
    
    return message;
  }
}; 